/**
 * Partner Bank Detail Page
 * /partner-bank/:slug — breakdown of each lending partner
 */

import { useParams, Link } from 'react-router-dom'
import { SEO } from '../components/common/SEO'
import { Card, CardContent } from '../components/common/Card'
import { Button } from '../components/common/Button'

interface PartnerBank {
  key: string
  name: string
  slug: string
  address: string
  country: string
  flag: string
  founded: string
  regulated: string
  website: string
  description: string
  longDescription: string
  interestRate: string
  ltv: string
  ltvLiquidation: string
  minLoan: string
  maxLoan: string
  loanTerms: string
  collateralTypes: string[]
  features: string[]
  process: { step: number; title: string; description: string }[]
}

const PARTNER_BANKS: Record<string, PartnerBank> = {
  amdax: {
    key: 'AMDAX',
    name: 'AMDAX',
    slug: 'amdax',
    address: '0x717d52e84eF30875De52834603c112675CeDB7CA',
    country: 'Netherlands',
    flag: '🇳🇱',
    founded: '2017',
    regulated: 'De Nederlandsche Bank (DNB) registered',
    website: 'https://amdax.com',
    description: 'Licensed digital asset exchange & custodian',
    longDescription: 'AMDAX is a Netherlands-based digital asset exchange and custodian registered with De Nederlandsche Bank (DNB). They provide institutional-grade infrastructure for digital asset trading, custody, and lending services across Europe. AMDAX focuses on regulatory compliance and security, serving both retail and institutional clients with transparent fee structures and segregated custody.',
    interestRate: '8.5 - 12%',
    ltv: 'Up to 50%',
    ltvLiquidation: '80%',
    minLoan: '$5,000',
    maxLoan: '$500,000',
    loanTerms: '30 days - 12 months',
    collateralTypes: ['BTC (Bitcoin)', 'ETH (Ethereum)', 'wstETH', 'cbBTC'],
    features: [
      'DNB-registered custodian',
      'Segregated cold storage custody',
      'Transparent fee structure',
      'European regulatory framework',
      'Institutional-grade security',
      'Automated collateral monitoring',
      'No prepayment penalties',
      'Multi-collateral support',
    ],
    process: [
      { step: 1, title: 'Select AMDAX', description: 'Choose AMDAX as your lending partner when creating a Letter of Credit.' },
      { step: 2, title: 'Deposit Collateral', description: 'Lock your digital assets as collateral in the on-chain vault.' },
      { step: 3, title: 'Application Review', description: 'AMDAX reviews your LOC application and collateral ratio.' },
      { step: 4, title: 'KYC Verification', description: 'Complete identity verification as required by Dutch regulations.' },
      { step: 5, title: 'Loan Disbursement', description: 'Upon approval, funds are disbursed to your designated account.' },
      { step: 6, title: 'Repay & Unlock', description: 'Repay the loan to release your collateral. No prepayment penalties.' },
    ],
  },
  empowermint: {
    key: 'EMPOWERMINT',
    name: 'Empowermint',
    slug: 'empowermint',
    address: '0x765365f5DF83A79001229f4E76881976039DB8F5',
    country: 'United States',
    flag: '🇺🇸',
    founded: '2021',
    regulated: 'US state-licensed digital asset lender',
    website: 'https://empowermint.io',
    description: 'Digital asset lending partner',
    longDescription: 'Empowermint is a US-based digital asset lending platform focused on making crypto-backed borrowing accessible and straightforward. They offer competitive rates with a streamlined application process, targeting both individual borrowers and small businesses looking to leverage their digital asset holdings without selling.',
    interestRate: '9 - 14%',
    ltv: 'Up to 60%',
    ltvLiquidation: '85%',
    minLoan: '$1,000',
    maxLoan: '$250,000',
    loanTerms: '30 days - 24 months',
    collateralTypes: ['BTC (Bitcoin)', 'ETH (Ethereum)', 'USDC', 'wstETH'],
    features: [
      'US state-licensed lender',
      'Higher LTV ratios available',
      'Flexible loan terms up to 24 months',
      'Lower minimum loan amount ($1,000)',
      'Automated margin call notifications',
      'Partial repayment options',
      'No origination fees',
      'Business lending available',
    ],
    process: [
      { step: 1, title: 'Select Empowermint', description: 'Choose Empowermint as your lending partner when creating a Letter of Credit.' },
      { step: 2, title: 'Deposit Collateral', description: 'Lock your digital assets as collateral in the on-chain vault.' },
      { step: 3, title: 'Quick Review', description: 'Empowermint reviews your application — typically within 24 hours.' },
      { step: 4, title: 'Identity Check', description: 'Complete a streamlined KYC process for US compliance.' },
      { step: 5, title: 'Loan Disbursement', description: 'Approved loans are disbursed quickly to your account.' },
      { step: 6, title: 'Repay & Unlock', description: 'Flexible repayment — partial or full. Collateral released upon completion.' },
    ],
  },
}

export default function PartnerBankPage() {
  const { slug } = useParams<{ slug: string }>()
  const bank = slug ? PARTNER_BANKS[slug] : null

  if (!bank) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Partner Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">The lending partner you're looking for doesn't exist.</p>
        <Link to="/locs">
          <Button variant="secondary">Back to LOCs</Button>
        </Link>
      </div>
    )
  }

  return (
    <>
      <SEO
        title={`${bank.name} — Lending Partner | Bitcoin.com Cash Loans`}
        description={bank.description}
        canonical={`/partner-bank/${bank.slug}`}
      />

      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Back link */}
        <Link
          to="/locs"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-8 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Letters of Credit
        </Link>

        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-orange/20 to-orange-100 dark:from-brand-orange/10 dark:to-orange-950/30 flex items-center justify-center text-3xl flex-shrink-0">
            {bank.flag}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{bank.name}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{bank.description}</p>
            <div className="flex flex-wrap gap-3 mt-3">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {bank.regulated}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium">
                {bank.flag} {bank.country}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium">
                Est. {bank.founded}
              </span>
            </div>
          </div>
        </div>

        {/* About */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">About {bank.name}</h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{bank.longDescription}</p>
          </CardContent>
        </Card>

        {/* Key Rates & Terms */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Rates & Terms</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { label: 'Interest Rate', value: bank.interestRate, accent: true },
                { label: 'Max LTV', value: bank.ltv, accent: true },
                { label: 'Liquidation LTV', value: bank.ltvLiquidation, accent: false },
                { label: 'Min Loan', value: bank.minLoan, accent: false },
                { label: 'Max Loan', value: bank.maxLoan, accent: false },
                { label: 'Loan Terms', value: bank.loanTerms, accent: false },
              ].map((item) => (
                <div key={item.label} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium mb-1">{item.label}</p>
                  <p className={`text-sm font-bold ${item.accent ? 'text-brand-orange' : 'text-gray-900 dark:text-white'}`}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Accepted Collateral */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Accepted Collateral</h2>
            <div className="flex flex-wrap gap-2">
              {bank.collateralTypes.map((col) => (
                <span
                  key={col}
                  className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-sm font-medium border border-blue-200 dark:border-blue-800"
                >
                  {col}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {bank.features.map((feature) => (
                <div key={feature} className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">How It Works</h2>
            <div className="space-y-4">
              {bank.process.map((step, idx) => (
                <div key={step.step} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-brand-orange text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {step.step}
                    </div>
                    {idx < bank.process.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 mt-2" />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{step.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <Link to="/locs">
            <Button variant="secondary" size="lg" className="inline-flex items-center gap-2 whitespace-nowrap">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              <span>Apply with {bank.name}</span>
            </Button>
          </Link>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            You'll be taken to the LOC creation form with {bank.name} pre-selected as your lending partner.
          </p>
        </div>
      </div>
    </>
  )
}
